import { Box, Breadcrumbs, Button, Link, Typography } from "@mui/material";
import { Home as HomeIcon, OpenInNew } from "@mui/icons-material";
import { GithubIcon } from "~/icons";
import { FormatAbbrDateYear } from "~/lib/date";
import { useDialogs } from "@toolpad/core";
import { WorkflowStatus } from "~/operations/types";
import WorkflowCancelDialog from "./WorkflowCancelDialog";
import useWorkflowDetail from "../hooks/useWorkflowDetail";
import { Link as RouterLink } from "react-router-dom";
import { useScope } from "~/hooks/useScope";

interface WorkflowDetailHeaderProps {
  headerData: ReturnType<typeof useWorkflowDetail>["headerData"];
}

export default function WorkflowHeader({
  headerData,
}: WorkflowDetailHeaderProps) {
  const dialogs = useDialogs();
  const { activeScope } = useScope();
  const {
    status,
    createdBy,
    createdAt,
    title,
    pullRequest,
    workflowMrn,
    integrationId,
  } = headerData;
  const { url = "", title: prTitle } = pullRequest || {};

  const handleOpenCancelDialog = async () => {
    const payload = {
      workflowMrn,
    };

    console.log("OPENING DIALOG WITH PAYLOAD", payload);
    await dialogs.open(WorkflowCancelDialog, payload);
  };

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${activeScope?.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/workflows"
      component={RouterLink}
      to={`/space/integrations/security_pipeline_github/${integrationId}/overview?${activeScope?.params}`}
      display="flex"
    >
      Workflows
    </Link>,
    <Typography key="/space/workflows/details/:workflowId">{title}</Typography>,
  ];

  return (
    <>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: 0.5,
            flex: "1 0 0",
          }}
        >
          <Typography variant="h2" sx={{ alignSelf: "stretch" }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body1">
                Created {FormatAbbrDateYear(createdAt)} by{" "}
                <Typography component="span" fontWeight="bold">
                  {createdBy?.name}
                </Typography>
              </Typography>
            </Box>
            {url && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Link
                  variant="body1"
                  color="inherit"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {prTitle}
                </Link>
                <OpenInNew sx={{ fontSize: 14 }} />
              </Box>
            )}
          </Box>
        </Box>
        {status !== WorkflowStatus.Canceled && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenCancelDialog}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<GithubIcon />}
          href={url}
          disabled={!url}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </Button>
      </Box>
    </>
  );
}
